import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { ClipboardList, Target, Play, Clock, Dumbbell, ExternalLink, ChevronDown, ChevronUp, User, Users } from 'lucide-react';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentTrainingPlan = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDrill, setExpandedDrill] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showCoachSelector, setShowCoachSelector] = useState(false);

  // Fetch available coaches for this athlete
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!athlete) return;
      
      const availableCoaches = [];
      
      // Check for private coach
      if (athlete.coach_id) {
        availableCoaches.push({
          id: athlete.coach_id,
          type: 'private',
          name: athlete.coach_name || 'Private Coach',
          icon: User
        });
      }
      
      // Check for team coaches
      try {
        const teamsRes = await axios.get(`${API}/parent/athlete/${athlete.id}/teams`, { headers: getAuthHeaders() });
        if (teamsRes.data?.teams) {
          teamsRes.data.teams.forEach(team => {
            if (team.coach_id) {
              availableCoaches.push({
                id: team.coach_id,
                type: 'team',
                name: team.coach_name || `${team.team_name} Coach`,
                teamName: team.team_name,
                teamId: team.team_id,
                icon: Users
              });
            }
          });
        }
      } catch (err) {
        console.log('No team data');
      }
      
      setCoaches(availableCoaches);
      if (availableCoaches.length > 0 && !selectedCoach) {
        setSelectedCoach(availableCoaches[0]);
      }
    };
    
    fetchCoaches();
  }, [athlete]);

  useEffect(() => {
    const fetchData = async () => {
      if (!athlete || !selectedCoach) return;
      setLoading(true);
      try {
        let url = `${API}/athletes/${athlete.id}/training-plan`;
        
        // If team coach, fetch team-specific training plan
        if (selectedCoach.type === 'team' && selectedCoach.teamId) {
          url = `${API}/parent/team/${selectedCoach.teamId}/athlete/${athlete.id}/training-plan`;
        }
        
        const planRes = await axios.get(url, { headers: getAuthHeaders() });
        setPlan(planRes.data);
      } catch (error) {
        console.error('Failed to fetch training plan:', error);
        setPlan(null);
      }
      setLoading(false);
    };
    fetchData();
  }, [athlete, selectedCoach, getAuthHeaders]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-400';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400';
      case 'advanced': return 'bg-red-500/20 text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'technical': return '⚽';
      case 'tactical': return '📋';
      case 'physical': return '💪';
      default: return '🎯';
    }
  };

  if (loading && !plan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Get drills from drills_full (new format) or drills (legacy)
  const planDrills = plan?.drills_full?.length > 0 ? plan.drills_full : plan?.drills || [];

  return (
    <div className="space-y-6" data-testid="parent-training-plan">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Training Plan</h1>
          <p className="text-muted-foreground">{athlete?.name}'s current training focus</p>
        </div>
        
        {/* Coach Selector */}
        {coaches.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setShowCoachSelector(!showCoachSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors"
              data-testid="coach-selector"
            >
              {selectedCoach && (
                <>
                  <selectedCoach.icon size={18} className="text-primary" />
                  <span className="text-sm font-medium">{selectedCoach.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary">
                    {selectedCoach.type === 'private' ? 'Private' : 'Team'}
                  </span>
                </>
              )}
              <ChevronDown size={16} className={`transition-transform ${showCoachSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {showCoachSelector && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
                <p className="px-3 py-2 text-xs text-muted-foreground border-b border-border">Select Coach View</p>
                {coaches.map((coach) => (
                  <button
                    key={`${coach.type}-${coach.id}`}
                    onClick={() => {
                      setSelectedCoach(coach);
                      setShowCoachSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-muted/50 transition-colors ${
                      selectedCoach?.id === coach.id && selectedCoach?.type === coach.type ? 'bg-primary/10' : ''
                    }`}
                  >
                    <coach.icon size={18} className={selectedCoach?.id === coach.id ? 'text-primary' : 'text-muted-foreground'} />
                    <div className="text-left">
                      <p className="text-sm font-medium">{coach.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {coach.type === 'private' ? 'Private Coach' : coach.teamName}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!plan || !plan.id ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            {selectedCoach ? `No training plan from ${selectedCoach.name}` : 'No training plan created yet'}
          </p>
          <p className="text-sm text-muted-foreground mt-2">Your coach will create a personalized training plan</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Plan Overview */}
          <div className="bg-card border border-border/40 p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl">{plan.name || 'Training Plan'}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{plan.plan_type} Plan</span>
                  <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded capitalize">{plan.sport}</span>
                </div>
              </div>
              {planDrills.length > 0 && (
                <div className="text-right">
                  <p className="text-2xl font-heading text-primary">{planDrills.length}</p>
                  <p className="text-xs text-muted-foreground">Drills</p>
                </div>
              )}
            </div>
            
            {plan.objectives && plan.objectives.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-primary mb-2">Training Objectives</h3>
                <ul className="space-y-2">
                  {plan.objectives.map((obj, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Target className="text-primary mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-sm">{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {plan.notes && (
              <div className="bg-muted/30 p-4 rounded-sm">
                <p className="text-sm font-medium text-primary mb-1">Coach Notes</p>
                <p className="text-sm text-muted-foreground italic">"{plan.notes}"</p>
              </div>
            )}
          </div>

          {/* Drills Section */}
          {planDrills.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Dumbbell className="text-primary" size={20} />
                <h3 className="font-heading text-lg">Your Drills</h3>
              </div>
              
              <div className="space-y-3">
                {planDrills.map((drill, idx) => (
                  <div 
                    key={drill.id || idx} 
                    className="bg-card border border-border/40 rounded-sm overflow-hidden"
                  >
                    {/* Drill Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-muted/20 transition-colors"
                      onClick={() => setExpandedDrill(expandedDrill === (drill.id || idx) ? null : (drill.id || idx))}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{getCategoryIcon(drill.category)}</span>
                            <span className="font-medium">{drill.name}</span>
                            {drill.video_url && (
                              <span className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">
                                <Play size={10} />
                                Video
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${getDifficultyColor(drill.difficulty)}`}>
                              {drill.difficulty}
                            </span>
                            <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.category}</span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock size={12} />
                              {drill.duration}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {expandedDrill === (drill.id || idx) ? (
                            <ChevronUp size={18} className="text-muted-foreground" />
                          ) : (
                            <ChevronDown size={18} className="text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Drill Details */}
                    {expandedDrill === (drill.id || idx) && (
                      <div className="border-t border-border/40 bg-muted/10 p-4 space-y-4">
                        {/* Description */}
                        <div>
                          <p className="text-sm font-medium text-primary mb-2">Description</p>
                          <div className="bg-muted/30 p-3 rounded-sm border border-border/40">
                            <MarkdownRenderer content={drill.description} />
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase">Duration</p>
                            <p className="text-sm font-medium">{drill.duration}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase">Difficulty</p>
                            <p className="text-sm font-medium capitalize">{drill.difficulty}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase">Skill Focus</p>
                            <p className="text-sm font-medium capitalize">{drill.skill_focus?.replace('_', ' ')}</p>
                          </div>
                          {drill.equipment && (
                            <div>
                              <p className="text-xs text-muted-foreground uppercase">Equipment</p>
                              <p className="text-sm font-medium">{drill.equipment}</p>
                            </div>
                          )}
                          {drill.position_focus && (
                            <div>
                              <p className="text-xs text-muted-foreground uppercase">Position</p>
                              <p className="text-sm font-medium">{drill.position_focus}</p>
                            </div>
                          )}
                        </div>

                        {/* Video Link */}
                        {drill.video_url && (
                          <div className="pt-2 border-t border-border/40">
                            <a
                              href={drill.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-primary text-black px-4 py-2 rounded-sm hover:bg-primary/90 transition-colors"
                            >
                              <Play size={16} />
                              <span className="font-medium">Watch Tutorial Video</span>
                              <ExternalLink size={14} />
                            </a>
                            <p className="text-xs text-muted-foreground mt-2">
                              Opens in a new tab
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Last updated: {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ParentTrainingPlan;
